import { useEffect, useState } from "react";

// The 地理定位 form state: {enabled, lat, lng, rotationDeg}. It is held in the
// plan editor and written only by 保存, like the floor size.

export const geoFormFrom = ({
  geoEnabled,
  geoOriginLat,
  geoOriginLng,
  geoRotationDeg,
}) => ({
  enabled: !!geoEnabled,
  lat: geoOriginLat ?? null,
  lng: geoOriginLng ?? null,
  rotationDeg: geoRotationDeg ?? 0,
});

export const canEnableGeo = (geo) => geo.lat !== null && geo.lng !== null;

const omitCleared = ({ cleared, ...rest }) => rest;

/**
 * 清除對齊: back to unset and off. `cleared` drives the 「已清除，按保存生效」
 * note; 保存 then writes the nulls through `geoPayload`.
 */
export const clearAlignment = (geo) => ({
  ...geo,
  enabled: false,
  lat: null,
  lng: null,
  rotationDeg: 0,
  aligned: false,
  cleared: true,
});

/** The chip text of the 地理定位 row. */
export const geoStatus = (geo) => {
  if (!canEnableGeo(geo)) return "尚未對齊";
  return geo.enabled ? "已對齊・啟用中" : "已對齊・未啟用";
};

/**
 * 套用 from 對齊地圖. The first one since the editor opened (or last saved)
 * also switches 地理定位 on; `aligned` drives the 「已對齊，按保存生效」 note
 * and never reaches the payload.
 */
export const applyAlignment = (geo, { lat, lng, rotationDeg }) => ({
  ...omitCleared(geo),
  lat,
  lng,
  rotationDeg,
  enabled: geo.aligned ? geo.enabled : true,
  aligned: true,
});

/**
 * The geo_* keys of the site PUT. A non-plane layout cannot carry geo (the
 * backend rejects it), so it only switches an enabled reference off and
 * leaves the stored values alone.
 */
export const geoPayload = (geo, layoutType) => {
  if (layoutType !== "plane") return geo.enabled ? { geo_enabled: false } : {};
  const set = canEnableGeo(geo);
  return {
    geo_enabled: geo.enabled,
    geo_origin_lat: set ? geo.lat : null,
    geo_origin_lng: set ? geo.lng : null,
    geo_rotation_deg: set ? geo.rotationDeg : null,
  };
};

/** Form state seeded from the loaded site; reloaded values and 取消 reset it. */
export const useGeoForm = (floorPlan, open) => {
  const { geoEnabled, geoOriginLat, geoOriginLng, geoRotationDeg } = floorPlan;
  const [geo, setGeo] = useState(() => geoFormFrom(floorPlan));
  useEffect(() => {
    setGeo(
      geoFormFrom({ geoEnabled, geoOriginLat, geoOriginLng, geoRotationDeg }),
    );
  }, [open, geoEnabled, geoOriginLat, geoOriginLng, geoRotationDeg]);
  return [geo, setGeo];
};
